import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BeforeAfterDiff } from '@/components/shared/BeforeAfterDiff';

interface Props {
  jobId: string | null;
}

interface RunEventRow {
  product_id: string;
  old_title: string | null;
  new_title: string | null;
}

/**
 * "Gerade fertig" — der juengste Titel DIESES Laufs.
 *
 * Vorher las die Query feed-weit: `products` gefiltert auf `feed_id`, sortiert
 * nach `last_optimized_at`. Bis der Lauf sein erstes Produkt geschrieben hatte,
 * stand unter "Gerade fertig" damit das juengste Produkt eines FRUEHEREN Laufs
 * — und auf einem Feed, der seit Wochen nicht optimiert wurde, minutenlang.
 * Dieselbe Wurzel wie das gestrichene Attribut-Panel und wie das feed-weite
 * Score-Histogramm aus Phase 4: das Panel sass im Fortschrittsfenster eines
 * Laufs und mass den Feed.
 *
 * Die Quelle ist deshalb `optimization_events`, nicht `products`:
 *   - `job_id` steht auf der Zeile, der Lauf-Bezug ist also KONSTRUKTIV da und
 *     kostet keine `.in(product_ids)`-Liste. Genau ein Request pro Poll, egal
 *     ob der Lauf 200 oder 10.756 Produkte umfasst; Index
 *     `idx_optimization_events_job_id` deckt das Praedikat.
 *   - `old_title`/`new_title` sind das Vorher/Nachher DIESES Laufs. Bei einem
 *     re-optimierten Produkt zeigt `products.title` inzwischen einen anderen
 *     Merchant-Stand und `optimized_title` womoeglich einen spaeteren Lauf —
 *     der Diff daraus vergleicht zwei Zeitpunkte, die nie zusammengehoerten.
 *   - Die Tabelle ist vom Client lesbar (SELECT-Policy fuer `authenticated`,
 *     Migration `20260311151310`); `FeedCheckInsights` liest sie live.
 *
 * Vor dem ersten getitelten Ergebnis des Laufs kommt keine Zeile zurueck, und
 * dann steht hier der Wartezustand ("Erste Texte werden geschrieben") statt
 * eines fremden Produkts.
 *
 * Kein Fertig-Zustand und kein Haken: das Modal rendert dieses Panel
 * ausschliesslich in der Phase `optimizing` (`!isSuccess && !isScoring`). Ist
 * der Lauf durch, wechselt es auf Score bzw. Erfolg und das Panel ist weg —
 * eine Abschlussbehauptung hier waere also konstruktionsbedingt nie wahr. Der
 * frueher dafuer benutzte `compact`-Schalter war ein Layout-Flag aus dem
 * entfernten 4-Schritt-Stepper und wurde vom Modal ohnehin nie gesetzt.
 */
export function Phase2Texte({ jobId }: Props) {
  const { t } = useTranslation('optimization');
  const { data } = useQuery({
    queryKey: ['progress-modal-phase2-run-latest', jobId],
    queryFn: async (): Promise<RunEventRow[]> => {
      if (!jobId) return [];
      const { data: rows } = await supabase
        .from('optimization_events')
        .select('product_id, old_title, new_title')
        .eq('job_id', jobId)
        .not('new_title', 'is', null)
        .order('optimized_at', { ascending: false })
        .limit(1);
      return (rows ?? []) as RunEventRow[];
    },
    refetchInterval: 2500,
    enabled: !!jobId,
  });

  const latest = data?.[0] ?? null;

  if (!latest?.new_title) {
    return (
      <div className="bg-muted rounded-lg p-4 mb-4">
        <div className="text-[10px] uppercase tracking-wider text-primary-ink font-bold mb-2">
          {t('progressModal.phase2.writingFirstTexts')}
        </div>
        <div className="bg-card rounded-lg p-3 h-24 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-muted rounded-lg p-4 mb-4">
      <div className="flex justify-between items-center mb-2.5">
        <span className="text-[10px] uppercase tracking-wider text-primary-ink font-bold">
          {t('progressModal.phase2.justFinished')}
        </span>
      </div>

      <div className="bg-card rounded-lg overflow-hidden border border-border p-3">
        <BeforeAfterDiff before={latest.old_title} after={latest.new_title} variant="stacked" />
      </div>
    </div>
  );
}
